import { ChangeDetectorRef, Component, Inject, KeyValueDiffers, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import { EnterNotesTableDelegate } from "ng/src/app/notes/enter-notes-table-delegate";
import { ShiftsService } from "ng/src/app/shifts/shifts.service";
import { IRouteService } from "app/shared/model/route.model";
import { LogService } from "ng/src/app/logs/log.service";
import { ILocationService } from "angular";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { ValueDiffer } from "ng/src/app/core/value-differ";
import { NoteControlService } from "ng/src/app/notes/note-controller/note-control.service";
import { fromMediaQuery, MediaQueries } from "ng/src/app/shared/media-query-observable";

@Component({
	selector: 'lb-enter-notes',
	templateUrl: 'enter-notes.component.html',
	styleUrls: ['enter-notes.component.scss'],
	providers: [EnterNotesTableDelegate]
})
export class EnterNotesComponent implements OnInit, OnDestroy {
	
	readonly narrowScreenQuery = fromMediaQuery(MediaQueries.NARROW_SCREEN);
	
	get canWrite() {
		return (this.selectedLog?.role ?? 'READ') !== 'READ';
	}
	
	get readOnlyView() {
		return this.$location.search()['readOnlyView'] === 'Y';
	}
	
	get remindersEnabled() {
		return this.$location.search()['disable-refresh'] !== 'Y';
	}
	
	get selectedLog() {
		return LogService.selectedLog;
	}
	
	private get tableMaximizedKey() {
		return `${this.selectedLog?.id}-enter-notes-maximized`;
	}
	
	private get remindersMaximizedKey() {
		return `${this.selectedLog?.id}-reminders-maximized`;
	}
	
	get tableMaximized() {
		return this._tableMaximized;
	}
	set tableMaximized(val) {
		this._tableMaximized = val;
		AppStateService.preferences.setValue(this.tableMaximizedKey, val);
	}
	
	get remindersMaximized() {
		return this._remindersMaximized;
	}
	set remindersMaximized(val) {
		this._remindersMaximized = val;
		AppStateService.preferences.setValue(this.remindersMaximizedKey, val);
	}
	
	get pendingSignoff() {
		return this._pendingSignoff;
	}
	set pendingSignoff(val) {
		this._pendingSignoff = val;
		this.tableSource.showPendingSignoffOnly = val && this.canWrite;
		this.$location.search('pendingSignoff', this.pendingSignoff ? 'Y' : null).replace();
		
		this.updateHeaderText();
	}
	
	get hasEnterNotesTemplateActive() {
		return !!this.noteControlService.enterNotesDetails;
	}
	
	headerText = '';
	notesPendingSignoff?: number;
	reminderCount = 0;
	templateSet = false;
	
	
	private _pendingSignoff = false;
	private locationDiffer?: ValueDiffer<string, any>;
	private _tableMaximized = AppStateService.preferences.getValue(this.tableMaximizedKey, false) && !('template' in this.$location.search());
	private _remindersMaximized = AppStateService.preferences.getValue(this.remindersMaximizedKey, true);
	private destroy = new Subject();
	
	constructor(
		public tableSource: EnterNotesTableDelegate,
		private shiftService: ShiftsService,
		private keyValueDiffers: KeyValueDiffers,
		private appStateService: AppStateService,
		private noteControlService: NoteControlService,
		private changeDetectRef: ChangeDetectorRef,
		@Inject('$location') private $location: ILocationService,
		@Inject('$route') private $route: IRouteService,
		@Inject('$rootScope') private $rootScope: LogBookRootScopeService
	) {}
	
	ngOnInit(): void {
		this.noteControlService.notePublished$.pipe(
			takeUntil(this.destroy)
		)
			.subscribe(() => this.onSave());
		
		this.shiftService.onShift$
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {
				if (!this.canWrite && this.pendingSignoff)
					this.pendingSignoff = false;
			});
		
		this.tableSource.signoffCount$
			.pipe(takeUntil(this.destroy))
			.subscribe(count => {
				this.notesPendingSignoff = count
				this.updateHeaderText();
			});
		
		LogBookRootScopeService.observeEvent(this.$rootScope, '$routeUpdate')
			.pipe(takeUntil(this.destroy))
			.subscribe(([event, route]) => {
				if (this.$location.search()['template']) {
					this.tableMaximized = false;
				}
			});
		
		this.locationDiffer = ValueDiffer.fromKeyValueDiffers(this.keyValueDiffers, () => this.$location.search());
		
		this.appStateService.locationChanged$.subscribe(() => {
			this.checkTemplateInLocation();
			this.locationDiffer?.changes()?.forEachChangedItem(record => {
				if (record.key !== 'log' || record.currentValue === record.previousValue) return;
				this.$route.reload();
			});
		});
		
		this.pendingSignoff = this.$location.search()['pendingSignoff'] === 'Y';
		this.checkTemplateInLocation();
	}
	
	ngOnDestroy(): void {
		this.noteControlService.enterNotesContainer = undefined;
		this.destroy.next();
	}
	
	moveNoteEntryToPopout() {
		if (!this.noteControlService.enterNotesDetails) return;
		this.noteControlService.sendToDialog(this.noteControlService.enterNotesDetails);
	}
	
	onSave() {
		this.tableSource.getNotes();
	}
	
	checkTemplateInLocation() {
		this.templateSet = 'template' in this.$location.search();
	}
	
	updateHeaderText() {
		if (this.pendingSignoff)
			this.headerText = `Showing signoff notes only. ${AppStateService.screenAction} to return to normal view.`;
		else if (this.notesPendingSignoff ?? 0 > 0)
			this.headerText = `${AppStateService.screenAction} to show ${this.notesPendingSignoff ?? ''}
				note${this.notesPendingSignoff !== 1 ? 's' : ''} pending your signoff.`;
		else
			this.headerText = '';
	}
	
	
	enterNotesContainerReady(ref: ViewContainerRef) {
		this.noteControlService.enterNotesContainer = ref;
		this.changeDetectRef.detectChanges();
	}
	
}